import { useCurrency } from '@/hooks/useCurrency'
import { DataTable, ReportPage, StatGrid, dateTimeLabel, titleCase, useReport, exportCsv, type Column } from '@/components/reports/ReportKit'

interface ModeRow {
  mode: string
  bills: number
  amount: number
  share: number
}
interface SplitBill {
  _id: string
  billNumber: string
  time: string | null
  total: number
  parts: { mode: string; amount: number }[]
}
interface PaymentData {
  modes: ModeRow[]
  splitBills: SplitBill[]
  summary: { totalCollected: number; expectedCash: number; splitCount: number }
}

export default function PaymentModesScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<PaymentData>('payment-modes')
  const data = report.data

  const modeColumns: Column<ModeRow>[] = [
    { key: 'mode', header: 'Mode', render: r => <span className="font-medium">{titleCase(r.mode)}</span> },
    { key: 'bills', header: 'Payments', align: 'right' },
    { key: 'amount', header: 'Amount', align: 'right', render: r => formatCurrency(r.amount) },
    {
      key: 'share',
      header: 'Share',
      align: 'right',
      render: r => (
        <div className="flex items-center justify-end gap-2">
          <div className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-muted sm:block">
            <div className="h-full bg-primary" style={{ width: `${Math.min(100, r.share)}%` }} />
          </div>
          <span>{r.share}%</span>
        </div>
      ),
      value: r => r.share
    }
  ]

  const splitColumns: Column<SplitBill>[] = [
    { key: 'billNumber', header: 'Bill', render: r => <span className="font-medium">#{r.billNumber}</span> },
    { key: 'time', header: 'Time', render: r => dateTimeLabel(r.time) },
    { key: 'parts', header: 'Split', render: r => r.parts.map(p => `${titleCase(p.mode)} ${formatCurrency(p.amount)}`).join(' · '), value: r => r.parts.map(p => `${p.mode} ${p.amount}`).join('; ') },
    { key: 'total', header: 'Bill total', align: 'right', render: r => formatCurrency(r.total) }
  ]

  return (
    <ReportPage
      eyebrow="Money · Settlement"
      title="Payment Modes"
      subtitle="Cash vs UPI vs card, split payments, and the cash you should have in the drawer"
      report={report}
      onExport={() => exportCsv('payment-modes', modeColumns, data?.modes ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Total collected', value: formatCurrency(data.summary.totalCollected) },
              { label: 'Expected cash in drawer', value: formatCurrency(data.summary.expectedCash), sub: 'Cash payments in this period' },
              { label: 'Payment modes used', value: data.modes.length },
              { label: 'Split-payment bills', value: data.summary.splitCount }
            ]}
          />
          <DataTable title="By payment mode" columns={modeColumns} rows={data.modes} rowKey={r => r.mode} defaultSort="amount" empty="No payments in this period." />
          <DataTable title="Split payments" hint="Bills settled with more than one mode" columns={splitColumns} rows={data.splitBills} rowKey={r => r._id} defaultSort="time" empty="No split-payment bills." />
        </>
      )}
    </ReportPage>
  )
}
